/**
 * AdminUsersTab Component
 * User management interface for administrators
 */
import { h } from 'preact';
import { useState, useEffect, useRef, useCallback } from 'preact/hooks';
import { formatDateTime } from '../../utils/dateUtils.js';
import { getAllUsers, getUsersCount, deleteUser, activateUser, deactivateUser, setEmailVerified, exportUsersReport, updateUser, createDemoUser, getUserById } from '../../services/adminService.js';
import { getAdminRole, hasAdminPermission } from '../../state/userState.js';
import { currentUser } from '../../state/userState.js';
import { publicConfig } from '../../state/publicConfig.js';
import { AdminUserQuickActionsModal } from './AdminUserQuickActionsModal.jsx';

const PAGE_SIZE = 50;

export function AdminUsersTab() {
  const adminRole = getAdminRole();
  const [users, setUsers] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [quickActionsUser, setQuickActionsUser] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [showCreateDemoModal, setShowCreateDemoModal] = useState(false);
  const [createDemoLoading, setCreateDemoLoading] = useState(false);
  const [demoForm, setDemoForm] = useState({ email: '', title: '', first_name: '', last_name: '', company: '', schedule_nurture: true });
  const searchTimeout = useRef(null);

  // Debounce search input — 500ms is the empirical sweet-spot for B2B
  // dashboards (long enough to not fire on every keystroke, short enough
  // to feel snappy). Enter forces immediate submit, Esc clears.
  useEffect(() => {
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }
    searchTimeout.current = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setCurrentPage(0); // Reset to first page on search
    }, 500);
    return () => clearTimeout(searchTimeout.current);
  }, [searchTerm]);

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      if (searchTimeout.current) clearTimeout(searchTimeout.current);
      setDebouncedSearch(searchTerm);
      setCurrentPage(0);
    } else if (e.key === 'Escape') {
      if (searchTimeout.current) clearTimeout(searchTimeout.current);
      setSearchTerm('');
      setDebouncedSearch('');
      setCurrentPage(0);
    }
  };

  // Load users when page or search changes
  useEffect(() => {
    loadUsers();
  }, [currentPage, debouncedSearch]);

  const loadUsers = async () => {
    setLoading(true);
    setError('');

    try {
      const [usersData, countData] = await Promise.all([
        getAllUsers({
          skip: currentPage * PAGE_SIZE,
          limit: PAGE_SIZE,
          search: debouncedSearch,
        }),
        getUsersCount(debouncedSearch),
      ]);

      if (Array.isArray(usersData)) {
        setUsers(usersData.map((user) => ({ ...user })));
        setTotalCount(countData.count);
      } else {
        setError('Invalid data format received from server');
      }
    } catch (err) {
      console.error('[AdminUsers] Load error:', err);
      setError(err?.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (userId, userEmail) => {
    if (!confirm(`Are you sure you want to delete user ${userEmail}? This action cannot be undone.`)) return;
    
    try {
      await deleteUser(userId);
      setSuccess(`User ${userEmail} deleted successfully`);
      await loadUsers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('[AdminUsers] Delete error:', err);
      setError(err?.message || 'Failed to delete user');
    }
  };

  const handleToggleActive = async (user) => {
    const action = user.is_active ? 'deactivate' : 'activate';
    if (!confirm(`Are you sure you want to ${action} user ${user.email}?`)) return;
    
    try {
      if (user.is_active) {
        await deactivateUser(user.id);
      } else {
        await activateUser(user.id);
      }
      setSuccess(`User ${user.email} ${action}d successfully`);
      await loadUsers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('[AdminUsers] Toggle active error:', err);
      setError(err?.message || `Failed to ${action} user`);
    }
  };

  const handleToggleEmailVerified = async (user) => {
    const action = user.email_verified ? 'unverify' : 'verify';
    if (!confirm(`Are you sure you want to mark ${user.email} as ${action === 'verify' ? 'verified' : 'unverified'}?`))
      return;

    try {
      await setEmailVerified(user.id, !user.email_verified);
      setSuccess(`Email for ${user.email} marked as ${!user.email_verified ? 'verified' : 'unverified'}`);
      // Update the selected user in place to reflect the change
      setSelectedUser({ ...user, email_verified: !user.email_verified });
      await loadUsers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('[AdminUsers] Toggle email verified error:', err);
      setError(err?.message || `Failed to ${action} email`);
    }
  };

  const handleSetAdminRole = async (userId, role) => {
    try {
      await updateUser(userId, { admin_role: role });
      // Update modal immediately so user sees the change
      setSelectedUser(prev => prev ? { ...prev, admin_role: role || null } : null);
      await loadUsers();
      const roleLabel = role ? role.replace(/_/g, ' ') : 'Standard User';
      setSuccess(`Role changed to ${roleLabel} successfully`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Failed to update admin role:', error);
      setError('Failed to update admin role: ' + (error.message || 'Unknown error'));
    }
  };

  // Ion knowledge approver capability (review + publish proposals). Independent
  // of admin_role — e.g. Michael is a Knowledge Expert AND an approver.
  const handleSetKnowledgeApprover = async (userId, value) => {
    try {
      await updateUser(userId, { ai_approver: value });
      setSelectedUser(prev => prev ? { ...prev, ai_approver: value } : null);
      await loadUsers();
      setSuccess(`Ion approver ${value ? 'granted' : 'revoked'} successfully`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Failed to update knowledge approver:', error);
      setError('Failed to update Ion approver: ' + (error.message || 'Unknown error'));
    }
  };

  // Segmentation tags (batemo/customer) — analytics metadata used to filter
  // Ion usage reports; NOT permissions (independent of admin_role). The `roles`
  // API field name stays as-is; only the UI label reads "Segment" so it isn't
  // mistaken for the admin_role permission select above.
  const handleToggleSegmentRole = async (userId, currentRoles, role) => {
    const next = currentRoles.includes(role)
      ? currentRoles.filter((r) => r !== role)
      : [...currentRoles, role];
    try {
      await updateUser(userId, { roles: next });
      setSelectedUser((prev) => (prev ? { ...prev, roles: next.sort() } : null));
      await loadUsers();
      setSuccess('Segment updated successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Failed to update segment:', error);
      setError('Failed to update segment: ' + (error.message || 'Unknown error'));
    }
  };

  // In-chat feedback capability (give feedback on Ion turns) — independent
  // of admin_role, e.g. a Knowledge Expert without any admin role.
  const handleSetFeedbackAccess = async (userId, value) => {
    try {
      await updateUser(userId, { feedback_access: value });
      setSelectedUser(prev => prev ? { ...prev, feedback_access: value } : null);
      await loadUsers();
      setSuccess(`Feedback access ${value ? 'granted' : 'revoked'} successfully`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Failed to update feedback access:', error);
      setError('Failed to update feedback access: ' + (error.message || 'Unknown error'));
    }
  };

  // When the detail modal opens from the list, the list row (AdminUserResponse)
  // carries no subscription info. Fetch the full detail once so the modal shows
  // the current tier/status and the grant control reflects reality.
  useEffect(() => {
    if (!selectedUser || selectedUser._detailLoaded) return;
    let cancelled = false;
    getUserById(selectedUser.id)
      .then((detail) => {
        if (cancelled) return;
        setSelectedUser((prev) =>
          prev && prev.id === detail.id
            ? { ...prev, subscription: detail.subscription ?? null, _detailLoaded: true }
            : prev
        );
      })
      .catch(() => {
        /* non-fatal — modal still works without subscription info */
      });
    return () => {
      cancelled = true;
    };
  }, [selectedUser?.id]);

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  // Date formatting uses centralized utility from dateUtils.js
  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    return formatDateTime(dateStr, 'de-DE');
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="admin-spinner"></div>
        <p>Loading users...</p>
      </div>
    );
  }

  return (
    <div className="admin-tab-content">
      <div className="admin-section-header">
        <h2>User Management</h2>
        <div className="admin-stats-row">
          <div className="admin-stat-badge">
            <span className="stat-number">{totalCount}</span>
            <span className="stat-label">Total Users</span>
          </div>
          <div className="admin-stat-badge admin-stat-badge--success">
            <span className="stat-number">{users.filter((u) => u.is_active).length}</span>
            <span className="stat-label">Active (page)</span>
          </div>
          <div className="admin-stat-badge admin-stat-badge--warning">
            <span className="stat-number">{users.filter((u) => !u.email_verified).length}</span>
            <span className="stat-label">Unverified (page)</span>
          </div>
          <div className="admin-stat-badge admin-stat-badge--info">
            <span className="stat-number">{users.filter((u) => u.admin_role).length}</span>
            <span className="stat-label">Admins (page)</span>
          </div>
        </div>
      </div>

      <div className="admin-header-actions">
        <div className="admin-search-wrapper">
          <svg className="admin-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8"/>
            <path d="M21 21l-4.35-4.35"/>
          </svg>
          <input
            type="text"
            className="admin-search-input"
            placeholder="Search by name or email…  (Enter = search now · Esc = clear)"
            value={searchTerm}
            onInput={(e) => setSearchTerm(e.target.value)}
            onKeyDown={handleSearchKeyDown}
          />
        </div>
        <button className="admin-btn admin-btn--secondary" onClick={loadUsers}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"/>
          </svg>
          Refresh
        </button>
        {adminRole === 'admin' && (
          <button
            className="admin-btn admin-btn--secondary"
            disabled={exporting}
            onClick={async () => {
              setExporting(true);
              setError('');
              try {
                await exportUsersReport();
                setSuccess('Users report downloaded successfully');
                setTimeout(() => setSuccess(''), 3000);
              } catch (err) {
                console.error('[AdminUsers] Export error:', err);
                setError(err?.message || 'Failed to export users report');
              } finally {
                setExporting(false);
              }
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="7 10 12 15 17 10"/>
              <line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
            {exporting ? 'Exporting...' : 'Export Excel'}
          </button>
        )}
        {/* Gated on the permission the endpoint enforces (users.demo_create),
            NOT on adminRole === 'admin': the two diverged silently once
            CUSTOMER_SUCCESS gained the underlying right. See permissions.py. */}
        {hasAdminPermission('users.demo_create') && (
          <button
            className="admin-btn admin-btn--primary"
            onClick={() => setShowCreateDemoModal(true)}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
              <circle cx="9" cy="7" r="4"/>
              <line x1="19" y1="8" x2="19" y2="14"/>
              <line x1="22" y1="11" x2="16" y2="11"/>
            </svg>
            Create Demo User
          </button>
        )}
      </div>

      {success && (
        <div className="admin-alert admin-alert--success">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
            <path d="M22 4L12 14.01l-3-3"/>
          </svg>
          {success}
        </div>
      )}

      {error && (
        <div className="admin-alert admin-alert--error">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
            <path d="M15 9l-6 6M9 9l6 6"/>
          </svg>
          {error}
        </div>
      )}

      {/* Quick-Actions Modal — re-send activation/password-reset emails,
          deactivate, delete. Self-contained: errors / confirmations are
          owned by the modal. After any action it calls onChanged so we
          refresh the user list. */}
      {quickActionsUser && (
        <AdminUserQuickActionsModal
          user={quickActionsUser}
          onClose={() => setQuickActionsUser(null)}
          onChanged={() => {
            loadUsers();
            // Close the modal if the user was deleted (no longer in list).
            // Otherwise leave it open so the admin can chain actions.
          }}
        />
      )}

      {/* User Detail Modal */}
      {selectedUser && (
        <div className="admin-modal-overlay" onClick={() => setSelectedUser(null)}>
          <div className="admin-modal admin-modal--glass" onClick={e => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>User Details</h3>
              <button className="admin-modal-close" onClick={() => setSelectedUser(null)}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12"/>
                </svg>
              </button>
            </div>
            <div className="admin-modal-body">
              <div className="admin-user-detail-grid">
                <div className="admin-detail-row">
                  <span className="admin-detail-label">Internal ID</span>
                  <span className="admin-detail-value admin-detail-value--monospace">{selectedUser.id}</span>
                </div>
                <div className="admin-detail-row">
                  <span className="admin-detail-label">Public ID</span>
                  <span className="admin-detail-value admin-detail-value--monospace">{selectedUser.public_id || 'N/A'}</span>
                </div>
                <div className="admin-detail-row">
                  <span className="admin-detail-label">Email</span>
                  <span className="admin-detail-value">{selectedUser.email}</span>
                </div>
                <div className="admin-detail-row">
                  <span className="admin-detail-label">Name</span>
                  <span className="admin-detail-value">{selectedUser.full_name || 'Not set'}</span>
                </div>
                <div className="admin-detail-row">
                  <span className="admin-detail-label">Title</span>
                  <span className="admin-detail-value">{selectedUser.title || 'None'}</span>
                </div>
                <div className="admin-detail-row">
                  <span className="admin-detail-label">Status</span>
                  <span className={`admin-badge ${selectedUser.is_active ? 'admin-badge--success' : 'admin-badge--error'}`}>
                    {selectedUser.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div className="admin-detail-row">
                  <span className="admin-detail-label">Email Verified</span>
                  <button
                    className={`admin-badge admin-badge--clickable ${selectedUser.email_verified ? 'admin-badge--success' : 'admin-badge--warning'}`}
                    onClick={() => handleToggleEmailVerified(selectedUser)}
                    title={`Click to ${selectedUser.email_verified ? 'unverify' : 'verify'} email`}
                    style={{ cursor: 'pointer', border: 'none', background: 'inherit' }}
                  >
                    {selectedUser.email_verified ? 'Verified' : 'Unverified'}
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      style={{ marginLeft: '6px', verticalAlign: 'middle' }}
                    >
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                    </svg>
                  </button>
                </div>
                <div className="admin-detail-row">
                  <span className="admin-detail-label">Role</span>
                  <span className={`admin-badge ${selectedUser.admin_role ? 'admin-badge--accent' : 'admin-badge--info'}`}>
                    {selectedUser.admin_role ? selectedUser.admin_role.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase()) : 'User'}
                  </span>
                </div>
                <div className="admin-detail-row">
                  <span className="admin-detail-label">Last Login</span>
                  <span className="admin-detail-value">{formatDate(selectedUser.last_login)}</span>
                </div>
                <div className="admin-detail-row">
                  <span className="admin-detail-label">Last Access</span>
                  <span className="admin-detail-value">{formatDate(selectedUser.last_access)}</span>
                </div>
                <div className="admin-detail-row">
                  <span className="admin-detail-label">Created</span>
                  <span className="admin-detail-value">{formatDate(selectedUser.created_at)}</span>
                </div>
                {selectedUser.subscription ? (
                  <>
                    <div className="admin-detail-divider">Subscription</div>
                    <div className="admin-detail-row">
                      <span className="admin-detail-label">Tier</span>
                      <span className="admin-badge admin-badge--accent">{selectedUser.subscription.tier}</span>
                    </div>
                    <div className="admin-detail-row">
                      <span className="admin-detail-label">Status</span>
                      <span className="admin-badge admin-badge--info">{selectedUser.subscription.status}</span>
                    </div>
                  </>
                ) : selectedUser._detailLoaded ? (
                  <>
                    <div className="admin-detail-divider">Subscription</div>
                    <div className="admin-detail-row">
                      <span className="admin-detail-label">Tier</span>
                      <span className="admin-badge admin-badge--warning">None</span>
                    </div>
                  </>
                ) : null}
              </div>
            </div>
            <div className="admin-modal-footer">
              {adminRole === 'admin' && (
                <div
                  style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                  title="Segment (analytics only) — used to filter Ion usage reports; not a permission"
                >
                  <span className="admin-detail-label" style={{ fontSize: 12 }}>Segment</span>
                  {['batemo', 'customer'].map((role) => {
                    const active = (selectedUser.roles || []).includes(role);
                    return (
                      <button
                        key={role}
                        type="button"
                        className={`admin-badge admin-badge--clickable ${
                          active ? 'admin-badge--accent' : 'admin-badge--inactive'
                        }`}
                        onClick={() =>
                          handleToggleSegmentRole(selectedUser.id, selectedUser.roles || [], role)
                        }
                      >
                        {role}
                      </button>
                    );
                  })}
                </div>
              )}
              {adminRole === 'admin' && (
                <select
                  value={selectedUser.admin_role || ''}
                  onChange={(e) => {
                    const newRole = e.target.value || null;
                    if (newRole === 'admin') {
                      const confirmed = window.confirm(
                        `Grant Admin to ${selectedUser.email}? This also grants Ion access and the Ion kill-switch bypass, applies immediately, and has no undo.`
                      );
                      if (!confirmed) {
                        // Revert the dropdown — a cancelled confirm must not leave it
                        // showing a role the user doesn't actually have.
                        e.target.value = selectedUser.admin_role || '';
                        return;
                      }
                    }
                    handleSetAdminRole(selectedUser.id, newRole);
                  }}
                  style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--border-primary, #333)', background: 'var(--bg-secondary, #1a1a1a)', color: 'var(--text-primary, #fff)', fontSize: '13px', cursor: 'pointer' }}
                  disabled={selectedUser.id === currentUser.value?.id}
                  title={selectedUser.id === currentUser.value?.id ? 'Cannot change own role' : 'Set admin role'}
                >
                  <option value="">Standard User</option>
                  <option value="admin">Admin</option>
                  <option value="marketing">Marketing</option>
                  <option value="customer_success">Customer Success</option>
                  <option value="finance">Finance</option>
                  <option value="knowledge_expert">Knowledge Expert</option>
                  <option value="application_specialist">Application Specialist</option>
                </select>
              )}
              {adminRole === 'admin' && (
                <button
                  type="button"
                  className={`admin-btn ${selectedUser.ai_approver ? 'admin-btn--warning' : 'admin-btn--ghost'}`}
                  onClick={() => handleSetKnowledgeApprover(selectedUser.id, !selectedUser.ai_approver)}
                  title="Ion knowledge: may review and publish proposals submitted by experts"
                >
                  {selectedUser.ai_approver ? '✓ Ion Approver' : 'Make Ion Approver'}
                </button>
              )}
              {adminRole === 'admin' && (
                <button
                  type="button"
                  className={`admin-btn ${selectedUser.feedback_access ? 'admin-btn--warning' : 'admin-btn--ghost'}`}
                  onClick={() => handleSetFeedbackAccess(selectedUser.id, !selectedUser.feedback_access)}
                  title="Ion: may give in-chat feedback on Ion responses"
                >
                  {selectedUser.feedback_access ? '✓ Feedback Access' : 'Grant Feedback Access'}
                </button>
              )}
              <button
                className={`admin-btn ${selectedUser.is_active ? 'admin-btn--warning' : 'admin-btn--success'}`}
                onClick={() => { handleToggleActive(selectedUser); setSelectedUser(null); }}
                disabled={!!selectedUser.admin_role}
              >
                {selectedUser.is_active ? 'Deactivate User' : 'Activate User'}
              </button>
              {adminRole === 'admin' && (
                <button
                  className="admin-btn admin-btn--danger"
                  onClick={() => { handleDelete(selectedUser.id, selectedUser.email); setSelectedUser(null); }}
                  disabled={!!selectedUser.admin_role}
                >
                  Delete User
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {users.length === 0 ? (
        <div className="admin-empty-state">
          <div className="admin-empty-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
          </div>
          <h3>No users found</h3>
          <p>{searchTerm ? 'Try adjusting your search criteria' : 'No users registered yet'}</p>
        </div>
      ) : (
        <>
          <div className="admin-table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Email</th>
                  <th>Status</th>
                  <th>Verified</th>
                  <th>Type</th>
                  <th>Last Login</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr
                    key={user.id}
                    className="admin-table-row--clickable"
                    onClick={() => setSelectedUser(user)}
                  >
                    <td>
                      <div className="admin-table-user">
                        <div className="user-avatar-small">
                          {(() => {
                            const f = String(user.first_name || '').charAt(0);
                            const l = String(user.last_name || '').charAt(0);
                            if (f && l) return (f + l).toUpperCase();
                            return (f || String(user.email || '').charAt(0) || 'U').toUpperCase();
                          })()}
                        </div>
                        <div>
                          <div className="user-name">{user.full_name || 'No name'}</div>
                          <div className="user-id">#{String(user.id || 'N/A')}</div>
                        </div>
                      </div>
                    </td>
                    <td>{String(user.email || 'N/A')}</td>
                    <td>
                      <span
                        className={`admin-badge ${user.is_active ? 'admin-badge--success' : 'admin-badge--error'}`}
                      >
                        {user.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <span
                        className={`admin-badge ${user.email_verified ? 'admin-badge--success' : 'admin-badge--warning'}`}
                      >
                        {user.email_verified ? 'Yes' : 'No'}
                      </span>
                    </td>
                    <td>
                      <span
                        className={`admin-badge ${user.admin_role ? 'admin-badge--accent' : 'admin-badge--info'}`}
                      >
                        {user.admin_role ? user.admin_role.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase()) : 'User'}
                      </span>
                    </td>
                    <td>{formatDate(user.last_login)}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <div className="admin-table-actions">
                        <button
                          className="admin-icon-btn admin-icon-btn--secondary"
                          title="Quick actions (resend emails, deactivate, delete)"
                          onClick={() => setQuickActionsUser(user)}
                        >
                          {/* Lightning bolt — quick actions affordance */}
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
                          </svg>
                        </button>
                        <button
                          className={`admin-icon-btn ${user.is_active ? 'admin-icon-btn--warning' : 'admin-icon-btn--success'}`}
                          title={user.is_active ? 'Deactivate user' : 'Activate user'}
                          onClick={() => handleToggleActive(user)}
                          disabled={!!user.admin_role}
                        >
                          {user.is_active ? (
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <circle cx="12" cy="12" r="10" />
                              <path d="M4.93 4.93l14.14 14.14" />
                            </svg>
                          ) : (
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                              <path d="M22 4L12 14.01l-3-3" />
                            </svg>
                          )}
                        </button>
                        {adminRole === 'admin' && (
                          <button
                            className="admin-icon-btn admin-icon-btn--danger"
                            title="Delete user"
                            onClick={() => handleDelete(user.id, user.email)}
                            disabled={!!user.admin_role}
                          >
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="admin-pagination">
              <button
                className="admin-btn admin-btn--secondary admin-btn--sm"
                onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
                disabled={currentPage === 0}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M15 18l-6-6 6-6" />
                </svg>
                Previous
              </button>
              <span className="admin-pagination-info">
                Page {currentPage + 1} of {totalPages} ({totalCount} users)
              </span>
              <button
                className="admin-btn admin-btn--secondary admin-btn--sm"
                onClick={() => setCurrentPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={currentPage >= totalPages - 1}
              >
                Next
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 18l6-6-6-6" />
                </svg>
              </button>
            </div>
          )}
        </>
      )}

      {/* Create Demo User Modal */}
      {showCreateDemoModal && (
        <div className="admin-modal-overlay" onClick={() => !createDemoLoading && setShowCreateDemoModal(false)}>
          <div className="admin-modal admin-modal--glass" onClick={e => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>Create Demo User</h3>
              <button className="admin-modal-close" onClick={() => !createDemoLoading && setShowCreateDemoModal(false)}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12"/>
                </svg>
              </button>
            </div>
            <div className="admin-modal-body">
              <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '13px', margin: '0 0 16px 0' }}>
                Creates a user with verified email, {publicConfig.value.demo_duration_days}-day DEMO access, and sends a welcome email with a password-set link.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'rgba(255,255,255,0.7)', marginBottom: '4px', textTransform: 'uppercase' }}>Email *</label>
                  <input
                    type="email"
                    value={demoForm.email}
                    onInput={e => setDemoForm(f => ({ ...f, email: e.target.value }))}
                    placeholder="visitor@company.com"
                    disabled={createDemoLoading}
                    style={{ width: '100%', padding: '10px 12px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff', fontSize: '14px' }}
                  />
                </div>
                <div>
                  {/* Persisted on the User row and prepended by User.full_name,
                      so it shows up in emails and PDFs. Registration has always
                      collected it; this modal did not, which left every
                      admin-created demo user permanently without a salutation. */}
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'rgba(255,255,255,0.7)', marginBottom: '4px', textTransform: 'uppercase' }}>Title (optional)</label>
                  <select
                    value={demoForm.title}
                    onChange={e => setDemoForm(f => ({ ...f, title: e.target.value }))}
                    disabled={createDemoLoading}
                    style={{ width: '100%', padding: '10px 12px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff', fontSize: '14px' }}
                  >
                    <option value="">-</option>
                    {['Dr.', 'Prof.', 'Prof. Dr.', 'Dr.-Ing.', 'Dipl.-Ing.', 'Mr.', 'Ms.', 'Mrs.'].map(t => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'rgba(255,255,255,0.7)', marginBottom: '4px', textTransform: 'uppercase' }}>First Name *</label>
                    <input
                      type="text"
                      value={demoForm.first_name}
                      onInput={e => setDemoForm(f => ({ ...f, first_name: e.target.value }))}
                      placeholder="John"
                      disabled={createDemoLoading}
                      style={{ width: '100%', padding: '10px 12px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff', fontSize: '14px' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'rgba(255,255,255,0.7)', marginBottom: '4px', textTransform: 'uppercase' }}>Last Name *</label>
                    <input
                      type="text"
                      value={demoForm.last_name}
                      onInput={e => setDemoForm(f => ({ ...f, last_name: e.target.value }))}
                      placeholder="Doe"
                      disabled={createDemoLoading}
                      style={{ width: '100%', padding: '10px 12px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff', fontSize: '14px' }}
                    />
                  </div>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'rgba(255,255,255,0.7)', marginBottom: '4px', textTransform: 'uppercase' }}>Company (optional)</label>
                  <input
                    type="text"
                    value={demoForm.company}
                    onInput={e => setDemoForm(f => ({ ...f, company: e.target.value }))}
                    placeholder="Acme Corp"
                    disabled={createDemoLoading}
                    style={{ width: '100%', padding: '10px 12px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff', fontSize: '14px' }}
                  />
                </div>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '10px 12px', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '8px', cursor: createDemoLoading ? 'not-allowed' : 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={demoForm.schedule_nurture}
                    onChange={e => setDemoForm(f => ({ ...f, schedule_nurture: e.target.checked }))}
                    disabled={createDemoLoading}
                    style={{ marginTop: '2px', cursor: createDemoLoading ? 'not-allowed' : 'pointer' }}
                  />
                  <span style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <span style={{ fontSize: '13px', fontWeight: 600, color: 'rgba(255,255,255,0.9)' }}>Marketing consent given — send nurture emails</span>
                    <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.55)' }}>Checked: opts the user in (consent timestamped) and schedules the Day 3 / 5 / 7 / 14 conversion emails. Unchecked: records an explicit opt-OUT. This is the user's consent answer, so uncheck it whenever they did not agree to be contacted.</span>
                  </span>
                </label>
              </div>
            </div>
            <div className="admin-modal-footer" style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', padding: '16px 24px', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
              <button
                className="admin-btn admin-btn--secondary"
                onClick={() => setShowCreateDemoModal(false)}
                disabled={createDemoLoading}
              >
                Cancel
              </button>
              <button
                className="admin-btn admin-btn--primary"
                disabled={createDemoLoading || !demoForm.email || !demoForm.first_name || !demoForm.last_name}
                onClick={async () => {
                  setCreateDemoLoading(true);
                  setError('');
                  try {
                    // `title` must be null, not '' — the backend field is an
                    // Optional[Title] enum and an empty string is a 422.
                    await createDemoUser({ ...demoForm, title: demoForm.title || null });
                    setSuccess(`Demo user ${demoForm.email} created successfully. Welcome email sent.`);
                    setShowCreateDemoModal(false);
                    setDemoForm({ email: '', title: '', first_name: '', last_name: '', company: '', schedule_nurture: true });
                    await loadUsers();
                    setTimeout(() => setSuccess(''), 5000);
                  } catch (err) {
                    setError(err?.message || 'Failed to create demo user');
                  } finally {
                    setCreateDemoLoading(false);
                  }
                }}
              >
                {createDemoLoading ? 'Creating...' : 'Create Demo User'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
